import {
  Inbox,
  Send,
  Sparkles,
  CalendarCheck,
  Trophy,
  Building2,
  Clock,
  Mail,
  MessageSquare,
  Phone,
  DollarSign,
  TrendingUp,
  Target,
  Bell,
  ArrowRight,
  User,
  ClipboardList,
  ChevronRight,
} from "lucide-react";

/* --- Data --------------------------------------------------------- */

const COLUMNS = [
  {
    id: "col-mql",
    key: "mql",
    title: "MQL Entered",
    count: 487,
    Icon: Inbox,
    accent: "#7A9BB5",
    subtitle: "This month",
    cards: [
      { id: "mql-1", name: "Dr. Sarah Chen", company: "Midwest Health Systems", source: "Trade Show", score: 87, days: 3 },
      { id: "mql-2", name: "Dr. Marcus Webb", company: "Regional Surgical", source: "LinkedIn Reply", score: 72, days: 5 },
      { id: "mql-3", name: "Amanda Torres", company: "Valley Medical Supply", source: "Trade Show", score: 45, days: 4 },
    ],
    moreCount: 483,
  },
  {
    id: "col-seq",
    key: "seq",
    title: "Sequence Active",
    count: 341,
    Icon: Send,
    accent: "#0EA5E9",
    subtitle: "Emails + SMS + Voice",
    cards: [
      { id: "seq-1", name: "Dr. Priya Nair", company: "Pacific Surgical", meta: { label: "Day 2 · SMS next", tone: "blue", Icon: MessageSquare } },
      { id: "seq-2", name: "Robert Kim", company: "Great Lakes Devices", meta: { label: "Day 6 · watch", tone: "amber", Icon: Clock } },
      { id: "seq-3", name: "Dr. Lisa Tran", company: "Summit Orthopedics", meta: { label: "Fast-Track Day 1", tone: "teal", Icon: Phone } },
    ],
    moreCount: 338,
  },
  {
    id: "col-eng",
    key: "eng",
    title: "Engaged",
    count: 127,
    Icon: Sparkles,
    accent: "#00C2A8",
    subtitle: "Opened · Clicked · Replied",
    cards: [
      { id: "eng-1", name: "Dr. Marcus Webb", company: "Regional Surgical", meta: { label: "Replied Day 3 email", tone: "green", Icon: Mail, live: true } },
      { id: "eng-2", name: "Amanda Torres", company: "Valley Medical", meta: { label: "Opened 3/5 emails", tone: "green", Icon: Mail } },
    ],
    moreCount: 125,
  },
  {
    id: "col-demo",
    key: "demo",
    title: "Demo Booked",
    count: 31,
    Icon: CalendarCheck,
    accent: "#00C2A8",
    subtitle: "Auto-scheduled · rep notified",
    highlight: true,
    cards: [
      {
        id: "demo-1",
        name: "James Park",
        company: "NovaMed Instruments",
        booked: "Jun 18",
        value: 14000,
        rep: "Taylor R.",
      },
      {
        id: "demo-2",
        name: "Dr. Lisa Tran",
        company: "Summit Orthopedics",
        booked: "Jun 19",
        value: 22000,
        rep: "Jordan M.",
      },
    ],
    moreCount: 29,
  },
  {
    id: "col-sql",
    key: "sql",
    title: "SQL Handed Off",
    count: 84,
    Icon: Trophy,
    accent: "#22C55E",
    subtitle: "Owned by rep · in-pipeline",
    win: true,
    cards: [
      {
        id: "sql-1",
        name: "Sarah Chen",
        company: "Midwest Health",
        value: 18500,
        prob: 68,
      },
    ],
    moreCount: 83,
  },
];

const HANDOFFS = [
  {
    id: "handoff-1",
    name: "James Park",
    company: "NovaMed Instruments",
    booked: "Jun 18 · 2:00 pm CT",
    context:
      "Responded to Day 2 SMS, clicked pricing overview, asked about GPO pricing.",
    talkingPoint:
      "They're evaluating 3 vendors, deadline is end of Q3.",
    rep: "Taylor R.",
    value: 14000,
    channel: "SMS",
  },
  {
    id: "handoff-2",
    name: "Dr. Lisa Tran",
    company: "Summit Orthopedics",
    booked: "Jun 19 · 10:00 am PT",
    context:
      "Inbound callback after voicemail. Asked about clinical evidence for orthopedic applications.",
    talkingPoint:
      "Send case study before call.",
    rep: "Jordan M.",
    value: 22000,
    channel: "Voice",
  },
];

const BENCHMARKS = [
  { id: "bench-yours", label: "This system", value: 17.3, color: "#0EA5E9", tag: "You are here" },
  { id: "bench-top",   label: "Blueprint top-performer benchmark", value: 21, color: "#00C2A8", tag: "Target" },
  { id: "bench-avg",   label: "Unmanaged industry average", value: 2, color: "#EF4444", tag: "Baseline" },
];

/* --- Helpers ------------------------------------------------------ */

function scoreColor(s) {
  if (s >= 80) return "#00C2A8";
  if (s >= 50) return "#0EA5E9";
  return "#F59E0B";
}
function toneColor(tone) {
  return {
    green: "#22C55E",
    teal: "#00C2A8",
    blue: "#0EA5E9",
    amber: "#F59E0B",
    muted: "#7A9BB5",
    red: "#EF4444",
  }[tone] || "#7A9BB5";
}
function fmtMoney(v) {
  return "$" + v.toLocaleString("en-US");
}
function channelIcon(ch) {
  if (ch === "Email") return Mail;
  if (ch === "SMS") return MessageSquare;
  if (ch === "Voice") return Phone;
  return Mail;
}

/* --- Kanban Card Renderers ---------------------------------------- */

function MQLCard({ c, i, accent }) {
  return (
    <div
      data-testid={c.id}
      className="card-glass p-3.5 enter-up"
      style={{ animationDelay: `${i * 40}ms`, borderLeft: `2px solid ${accent}` }}
    >
      <div className="flex items-center justify-between mb-1">
        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
          {c.name}
        </div>
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 600,
            fontSize: 11.5,
            color: scoreColor(c.score),
            padding: "2px 6px",
            borderRadius: 4,
            background: `${scoreColor(c.score)}12`,
            border: `1px solid ${scoreColor(c.score)}30`,
          }}
        >
          {c.score}
        </span>
      </div>
      <div className="flex items-center gap-1.5 mb-2" style={{ fontFamily: "Inter, sans-serif", fontSize: 11.5, color: "var(--text-secondary)" }}>
        <Building2 size={11} color="var(--text-muted)" />
        {c.company}
      </div>
      <div className="flex items-center justify-between">
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 9.5,
            color: "var(--text-muted)",
            letterSpacing: "0.06em",
            padding: "2px 6px",
            borderRadius: 3,
            background: "rgba(30, 48, 72, 0.5)",
          }}
        >
          {c.source}
        </span>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "var(--text-muted)" }}>
          {c.days}d in system
        </span>
      </div>
    </div>
  );
}

function SequenceCard({ c, i, accent }) {
  const { Icon } = c.meta;
  const color = toneColor(c.meta.tone);
  return (
    <div
      data-testid={c.id}
      className="card-glass p-3.5 enter-up"
      style={{ animationDelay: `${i * 40}ms`, borderLeft: `2px solid ${accent}` }}
    >
      <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
        {c.name}
      </div>
      <div className="flex items-center gap-1.5 mt-1 mb-2" style={{ fontFamily: "Inter, sans-serif", fontSize: 11.5, color: "var(--text-secondary)" }}>
        <Building2 size={11} color="var(--text-muted)" />
        {c.company}
      </div>
      <div
        className="inline-flex items-center gap-1.5"
        style={{
          padding: "3px 8px",
          borderRadius: 4,
          background: `${color}12`,
          border: `1px solid ${color}30`,
          color: color,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 10.5,
          letterSpacing: "0.04em",
        }}
      >
        <Icon size={10} />
        {c.meta.label}
      </div>
    </div>
  );
}

function EngagedCard({ c, i, accent }) {
  const { Icon, live } = c.meta;
  const color = toneColor(c.meta.tone);
  return (
    <div
      data-testid={c.id}
      className="card-glass p-3.5 enter-up"
      style={{ animationDelay: `${i * 40}ms`, borderLeft: `2px solid ${accent}` }}
    >
      <div className="flex items-center gap-2 mb-1">
        <span
          className={live ? "pill-dot-pulse" : ""}
          style={{
            width: 8, height: 8, borderRadius: 999,
            background: "#22C55E",
            boxShadow: "0 0 8px rgba(34, 197, 94, 0.6)",
            flexShrink: 0,
          }}
        />
        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, fontWeight: 600, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
          {c.name}
        </div>
      </div>
      <div className="flex items-center gap-1.5 mb-2" style={{ fontFamily: "Inter, sans-serif", fontSize: 11.5, color: "var(--text-secondary)" }}>
        <Building2 size={11} color="var(--text-muted)" />
        {c.company}
      </div>
      <div
        className="inline-flex items-center gap-1.5"
        style={{
          padding: "3px 8px",
          borderRadius: 4,
          background: `${color}12`,
          border: `1px solid ${color}30`,
          color: color,
          fontFamily: "Inter, sans-serif",
          fontSize: 11,
          fontWeight: 500,
        }}
      >
        <Icon size={10} />
        {c.meta.label}
      </div>
    </div>
  );
}

function DemoCard({ c, i, accent }) {
  return (
    <div
      data-testid={c.id}
      className="card-glass card-interactive p-4 enter-up relative overflow-hidden"
      style={{
        animationDelay: `${i * 50}ms`,
        border: `1px solid ${accent}40`,
        background: `linear-gradient(135deg, ${accent}0B 0%, transparent 100%), var(--bg-elevated)`,
        boxShadow: `0 0 20px ${accent}12`,
      }}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
            {c.name}
          </div>
          <div className="flex items-center gap-1.5 mt-1" style={{ fontFamily: "Inter, sans-serif", fontSize: 11.5, color: "var(--text-secondary)" }}>
            <Building2 size={11} color="var(--text-muted)" />
            {c.company}
          </div>
        </div>
        <CalendarCheck size={14} color={accent} />
      </div>
      <div
        className="mt-3 pt-3 grid grid-cols-2 gap-2"
        style={{ borderTop: `1px dashed ${accent}30` }}
      >
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: "0.10em", color: "var(--text-muted)", textTransform: "uppercase" }}>
            Booked
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "var(--text-primary)", fontWeight: 500, marginTop: 1 }}>
            {c.booked}
          </div>
        </div>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: "0.10em", color: "var(--text-muted)", textTransform: "uppercase" }}>
            Value
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: accent, fontWeight: 600, marginTop: 1 }}>
            {fmtMoney(c.value)}
          </div>
        </div>
        <div className="col-span-2">
          <div className="flex items-center gap-1.5">
            <User size={11} color="var(--text-muted)" />
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: "var(--text-secondary)" }}>
              Rep · {c.rep}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function SqlCard({ c, i }) {
  return (
    <div
      data-testid={c.id}
      className="card-glass card-interactive p-4 enter-up relative"
      style={{
        animationDelay: `${i * 50}ms`,
        borderLeft: "3px solid #22C55E",
        background: "linear-gradient(135deg, rgba(34, 197, 94, 0.06) 0%, transparent 100%), var(--bg-elevated)",
        boxShadow: "0 0 22px rgba(34, 197, 94, 0.10)",
      }}
    >
      <div className="flex items-start justify-between mb-2">
        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
          {c.name}
        </div>
        <Trophy size={13} color="#22C55E" />
      </div>
      <div className="flex items-center gap-1.5 mb-3" style={{ fontFamily: "Inter, sans-serif", fontSize: 11.5, color: "var(--text-secondary)" }}>
        <Building2 size={11} color="var(--text-muted)" />
        {c.company}
      </div>
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: "0.10em", color: "var(--text-muted)", textTransform: "uppercase" }}>
            Deal
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 15, color: "#22C55E", fontWeight: 700, letterSpacing: "-0.01em" }}>
            {fmtMoney(c.value)}
          </div>
        </div>
        <div className="text-right">
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, letterSpacing: "0.10em", color: "var(--text-muted)", textTransform: "uppercase" }}>
            Close prob.
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "var(--text-primary)", fontWeight: 600 }}>
            {c.prob}%
          </div>
        </div>
      </div>
      <div className="progress-track" style={{ background: "rgba(34, 197, 94, 0.15)" }}>
        <div className="progress-fill" style={{ width: `${c.prob}%`, background: "linear-gradient(90deg, #22C55E, #00C2A8)" }} />
      </div>
    </div>
  );
}

function KanbanColumn({ col, idx }) {
  const { Icon } = col;
  return (
    <div
      data-testid={col.id}
      className="flex-shrink-0 flex flex-col enter-up"
      style={{
        width: 280,
        animationDelay: `${idx * 80}ms`,
      }}
    >
      {/* Column header */}
      <div
        className="rounded-lg p-3 mb-3"
        style={{
          background: col.highlight
            ? `linear-gradient(135deg, ${col.accent}18, ${col.accent}08)`
            : "var(--bg-elevated)",
          border: `1px solid ${col.accent}${col.highlight ? "40" : "25"}`,
        }}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <div
              className="flex items-center justify-center rounded-md"
              style={{
                width: 24, height: 24,
                background: `${col.accent}18`,
                border: `1px solid ${col.accent}30`,
              }}
            >
              <Icon size={12} color={col.accent} />
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10.5,
                letterSpacing: "0.14em",
                color: col.accent,
                textTransform: "uppercase",
                fontWeight: 600,
              }}
            >
              {col.title}
            </div>
          </div>
          <span
            data-testid={`${col.id}-count`}
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 700,
              fontSize: 14,
              color: "var(--text-primary)",
              padding: "2px 9px",
              borderRadius: 999,
              background: `${col.accent}18`,
              border: `1px solid ${col.accent}30`,
              letterSpacing: "-0.01em",
            }}
          >
            {col.count}
          </span>
        </div>
        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--text-muted)" }}>
          {col.subtitle}
        </div>
      </div>

      {/* Cards */}
      <div className="flex flex-col gap-2.5">
        {col.cards.map((c, i) => {
          if (col.key === "mql") return <MQLCard key={c.id} c={c} i={i} accent={col.accent} />;
          if (col.key === "seq") return <SequenceCard key={c.id} c={c} i={i} accent={col.accent} />;
          if (col.key === "eng") return <EngagedCard key={c.id} c={c} i={i} accent={col.accent} />;
          if (col.key === "demo") return <DemoCard key={c.id} c={c} i={i} accent={col.accent} />;
          if (col.key === "sql") return <SqlCard key={c.id} c={c} i={i} />;
          return null;
        })}
        {/* +N more */}
        {col.moreCount > 0 && (
          <div
            className="rounded-lg text-center"
            style={{
              padding: "10px 12px",
              background: "rgba(15, 25, 35, 0.4)",
              border: "1px dashed var(--border-divider)",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              color: "var(--text-muted)",
              letterSpacing: "0.06em",
            }}
          >
            + {col.moreCount.toLocaleString()} more
          </div>
        )}
      </div>
    </div>
  );
}

function Kanban() {
  return (
    <section data-testid="pipeline-kanban" className="card-glass p-5">
      <header className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{ width: 28, height: 28, background: "rgba(0, 194, 168, 0.10)", border: "1px solid rgba(0, 194, 168, 0.25)" }}
          >
            <ClipboardList size={14} color="#00C2A8" />
          </div>
          <div>
            <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 15, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              Pipeline Kanban
            </h2>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              MQL → SQL · live · this month
            </div>
          </div>
        </div>
        <span className="pill pill-teal">
          <span className="pill-dot pill-dot-pulse" />
          Streaming
        </span>
      </header>

      <div
        className="flex gap-4 overflow-x-auto pb-3"
        style={{ scrollSnapType: "x mandatory" }}
      >
        {COLUMNS.map((col, i) => (
          <div key={col.id} style={{ scrollSnapAlign: "start" }}>
            <KanbanColumn col={col} idx={i} />
          </div>
        ))}
      </div>
    </section>
  );
}

function AcquisitionRecovery() {
  return (
    <article data-testid="acquisition-recovery" className="card-glass p-6">
      <header className="flex items-center gap-2.5 mb-5">
        <div
          className="flex items-center justify-center rounded-md"
          style={{ width: 28, height: 28, background: "rgba(245, 158, 11, 0.10)", border: "1px solid rgba(245, 158, 11, 0.30)" }}
        >
          <DollarSign size={14} color="#F59E0B" />
        </div>
        <div>
          <h3 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 15, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
            Acquisition Cost Recovery
          </h3>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
            Every MQL is a paid asset · June 2026
          </div>
        </div>
      </header>

      {/* Cost math visualization */}
      <div
        className="rounded-lg p-4 mb-4"
        style={{
          background: "rgba(15, 25, 35, 0.5)",
          border: "1px solid var(--border-divider)",
        }}
      >
        <div className="grid grid-cols-3 items-end gap-4">
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 4 }}>
              Leads
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 22, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              487
            </div>
          </div>
          <div className="text-center" style={{ color: "var(--text-muted)", fontFamily: "'JetBrains Mono', monospace" }}>
            ×
          </div>
          <div className="text-right">
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 4 }}>
              Avg CAC
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 22, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              $214
            </div>
          </div>
        </div>
        <div
          className="mt-3 pt-3 flex items-center justify-between"
          style={{ borderTop: "1px dashed rgba(245, 158, 11, 0.30)" }}
        >
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--text-muted)", letterSpacing: "0.08em", textTransform: "uppercase" }}>
            Total investment
          </span>
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 700,
              fontSize: 22,
              color: "#F59E0B",
              letterSpacing: "-0.01em",
            }}
          >
            $104,118
          </span>
        </div>
      </div>

      {/* SQL result strip */}
      <div className="grid grid-cols-2 gap-3">
        <div
          className="rounded-lg p-3"
          style={{
            background: "rgba(0, 194, 168, 0.08)",
            border: "1px solid rgba(0, 194, 168, 0.25)",
          }}
        >
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 3 }}>
            Your cost per SQL
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 22, color: "#00C2A8", letterSpacing: "-0.01em" }}>
            $1,240
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: "var(--text-secondary)", marginTop: 2 }}>
            84 SQLs generated
          </div>
        </div>
        <div
          className="rounded-lg p-3"
          style={{
            background: "rgba(239, 68, 68, 0.06)",
            border: "1px solid rgba(239, 68, 68, 0.25)",
          }}
        >
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 3 }}>
            Industry avg (manual)
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 22, color: "#EF4444", letterSpacing: "-0.01em" }}>
            $10,700+
          </div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: "var(--text-secondary)", marginTop: 2 }}>
            per SQL · 8.6× yours
          </div>
        </div>
      </div>
    </article>
  );
}

function BenchmarkBar({ item, max, i }) {
  const width = (item.value / max) * 100;
  return (
    <div
      data-testid={item.id}
      className="enter-up"
      style={{ animationDelay: `${i * 100}ms` }}
    >
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <span
            style={{
              width: 8, height: 8, borderRadius: 999,
              background: item.color,
              boxShadow: `0 0 8px ${item.color}66`,
            }}
          />
          <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--text-primary)", fontWeight: 500 }}>
            {item.label}
          </span>
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9.5,
              letterSpacing: "0.10em",
              color: item.color,
              padding: "2px 5px",
              borderRadius: 3,
              background: `${item.color}12`,
              border: `1px solid ${item.color}30`,
              textTransform: "uppercase",
            }}
          >
            {item.tag}
          </span>
        </div>
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 700,
            fontSize: 15,
            color: item.color,
            letterSpacing: "-0.01em",
          }}
        >
          {item.value}%
        </span>
      </div>
      <div
        className="progress-track"
        style={{ height: 10, background: "rgba(30, 48, 72, 0.5)" }}
      >
        <div
          className="progress-fill"
          style={{
            width: `${width}%`,
            background: `linear-gradient(90deg, ${item.color}, ${item.color}CC)`,
            boxShadow: `0 0 10px ${item.color}44`,
          }}
        />
      </div>
    </div>
  );
}

function BenchmarkPanel() {
  const max = Math.max(...BENCHMARKS.map((b) => b.value));
  return (
    <article data-testid="benchmark-panel" className="card-glass p-6">
      <header className="flex items-center gap-2.5 mb-5">
        <div
          className="flex items-center justify-center rounded-md"
          style={{ width: 28, height: 28, background: "rgba(14, 165, 233, 0.10)", border: "1px solid rgba(14, 165, 233, 0.30)" }}
        >
          <Target size={14} color="#0EA5E9" />
        </div>
        <div>
          <h3 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 15, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
            Conversion Rate vs. Benchmark
          </h3>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
            MQL → SQL rate · June 2026
          </div>
        </div>
      </header>

      <div className="space-y-4 mb-5">
        {BENCHMARKS.map((b, i) => <BenchmarkBar key={b.id} item={b} max={max} i={i} />)}
      </div>

      {/* Gap analysis */}
      <div
        className="rounded-lg p-4"
        style={{
          background:
            "linear-gradient(135deg, rgba(0, 194, 168, 0.06) 0%, rgba(14, 165, 233, 0.03) 100%)",
          border: "1px solid rgba(0, 194, 168, 0.25)",
        }}
      >
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp size={13} color="#00C2A8" />
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.14em",
              color: "#00C2A8",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            Gap Analysis
          </span>
        </div>
        <div
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 13,
            lineHeight: 1.55,
            color: "var(--text-primary)",
            marginBottom: 10,
          }}
        >
          Target: <span style={{ color: "#00C2A8", fontWeight: 600 }}>21%</span>. You're at{" "}
          <span style={{ color: "#0EA5E9", fontWeight: 600 }}>17.3%</span>.
        </div>

        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            letterSpacing: "0.10em",
            color: "var(--text-muted)",
            textTransform: "uppercase",
            marginBottom: 6,
          }}
        >
          3 Levers to Close
        </div>

        <ol className="space-y-2">
          {[
            "Increase SMS usage on Warm segment",
            "Shorten Fast-Track voice delay to Day 0",
            "Add Day 8 re-touch to 7-Day sequence",
          ].map((lever, i) => (
            <li
              key={i}
              data-testid={`lever-${i}`}
              className="flex items-start gap-2.5"
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 999,
                  background: "rgba(0, 194, 168, 0.10)",
                  border: "1px solid rgba(0, 194, 168, 0.30)",
                  color: "#00C2A8",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 10,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                {i + 1}
              </span>
              <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--text-primary)", lineHeight: 1.5 }}>
                {lever}
              </span>
            </li>
          ))}
        </ol>
      </div>
    </article>
  );
}

function HandoffCard({ h, i }) {
  const ChannelIcon = channelIcon(h.channel);
  return (
    <article
      data-testid={h.id}
      className="card-glass p-5 enter-up"
      style={{
        animationDelay: `${i * 100}ms`,
        borderLeft: "3px solid #22C55E",
        background: "linear-gradient(135deg, rgba(34, 197, 94, 0.04) 0%, transparent 100%), var(--bg-elevated)",
      }}
    >
      {/* Top */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="flex items-center justify-center rounded-md relative"
            style={{ width: 34, height: 34, background: "rgba(34, 197, 94, 0.10)", border: "1px solid rgba(34, 197, 94, 0.30)" }}
          >
            <Bell size={15} color="#22C55E" />
            <span
              className="pill-dot-pulse"
              style={{
                position: "absolute",
                top: -3,
                right: -3,
                width: 8,
                height: 8,
                borderRadius: 999,
                background: "#22C55E",
              }}
            />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div style={{ fontFamily: "Inter, sans-serif", fontSize: 14, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
                {h.name}
              </div>
              <span className="pill pill-green" style={{ fontSize: 9.5 }}>
                <span className="pill-dot" />
                Booked
              </span>
            </div>
            <div className="flex items-center gap-1.5 mt-1" style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--text-secondary)" }}>
              <Building2 size={11} color="var(--text-muted)" />
              {h.company}
              <span style={{ color: "var(--text-muted)" }}>·</span>
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                  color: "var(--text-secondary)",
                }}
              >
                {h.booked}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 700,
              fontSize: 16,
              color: "#22C55E",
              letterSpacing: "-0.01em",
            }}
          >
            {fmtMoney(h.value)}
          </div>
          <div
            className="inline-flex items-center gap-1.5"
            style={{
              padding: "2px 6px",
              borderRadius: 4,
              background: `${toneColor(h.channel === "SMS" ? "blue" : h.channel === "Voice" ? "teal" : "amber")}12`,
              border: `1px solid ${toneColor(h.channel === "SMS" ? "blue" : h.channel === "Voice" ? "teal" : "amber")}30`,
              color: toneColor(h.channel === "SMS" ? "blue" : h.channel === "Voice" ? "teal" : "amber"),
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9.5,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            <ChannelIcon size={10} />
            {h.channel} conversion
          </div>
        </div>
      </div>

      {/* Context + talking point */}
      <div
        className="rounded-lg p-3.5 mb-3"
        style={{
          background: "rgba(15, 25, 35, 0.5)",
          border: "1px solid var(--border-divider)",
        }}
      >
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 4 }}>
          Context
        </div>
        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "var(--text-primary)", lineHeight: 1.55, marginBottom: 10 }}>
          {h.context}
        </div>

        <div className="flex items-start gap-2">
          <ArrowRight size={13} color="#00C2A8" style={{ marginTop: 3, flexShrink: 0 }} />
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "#00C2A8", textTransform: "uppercase", marginBottom: 2 }}>
              Suggested talking point
            </div>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--text-primary)", lineHeight: 1.5 }}>
              {h.talkingPoint}
            </div>
          </div>
        </div>
      </div>

      {/* Assigned rep */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            style={{
              width: 24, height: 24, borderRadius: 999,
              background: "linear-gradient(135deg, #0EA5E9, #00C2A8)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "Inter, sans-serif", fontSize: 10, fontWeight: 700,
              color: "#0F1923",
            }}
          >
            {h.rep.split(" ").map((s) => s[0]).join("")}
          </div>
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: "0.10em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              Assigned rep
            </div>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--text-primary)", fontWeight: 500 }}>
              {h.rep}
            </div>
          </div>
        </div>
        <div
          className="inline-flex items-center gap-1.5"
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
            color: "#00C2A8",
            padding: "6px 10px",
            borderRadius: 6,
            background: "rgba(0, 194, 168, 0.08)",
            border: "1px solid rgba(0, 194, 168, 0.25)",
            fontWeight: 500,
          }}
        >
          CRM task created
          <ChevronRight size={12} />
        </div>
      </div>
    </article>
  );
}

function RepHandoffFeed() {
  return (
    <section data-testid="rep-handoff-feed" className="space-y-4">
      <div>
        <div className="flex items-center gap-3">
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.14em",
              color: "var(--text-muted)",
              textTransform: "uppercase",
            }}
          >
            Rep Handoff Feed
          </div>
          <div style={{ flex: 1, height: 1, background: "var(--border-divider)" }} />
        </div>
        <p
          className="mt-3"
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 13.5,
            color: "var(--text-secondary)",
            lineHeight: 1.5,
            maxWidth: 700,
          }}
        >
          When a lead converts to SQL, the rep receives a CRM task with full context automatically —
          channel of conversion, engagement history, and an AI-generated talking point.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {HANDOFFS.map((h, i) => <HandoffCard key={h.id} h={h} i={i} />)}
      </div>
    </section>
  );
}

/* --- Page --------------------------------------------------------- */

export default function Pipeline() {
  return (
    <div data-testid="pipeline-page" className="max-w-[1400px] mx-auto space-y-8">
      {/* Header */}
      <section data-testid="pipeline-header" className="enter-up">
        <div className="flex items-start gap-4">
          <div
            style={{
              width: 4,
              alignSelf: "stretch",
              background: "linear-gradient(180deg, #00C2A8, #22C55E)",
              borderRadius: 4,
              minHeight: 68,
            }}
          />
          <div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                letterSpacing: "0.14em",
                color: "var(--accent-teal)",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              Module · Full-funnel visibility
            </div>
            <h1
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 800,
                fontSize: 28,
                letterSpacing: "-0.02em",
                color: "var(--text-primary)",
                lineHeight: 1.2,
                marginBottom: 8,
              }}
            >
              Conversion Pipeline
            </h1>
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 14,
                lineHeight: 1.55,
                color: "var(--text-secondary)",
                maxWidth: 820,
              }}
            >
              From <span style={{ color: "#EF4444", fontWeight: 600 }}>2%</span> to{" "}
              <span style={{ color: "#00C2A8", fontWeight: 600 }}>21%</span>: every MQL tracked from entry to SQL. Acquisition cost recovered, demo booked, rep notified.
            </p>
          </div>
        </div>
      </section>

      {/* Kanban */}
      <Kanban />

      {/* 2-panel stats */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AcquisitionRecovery />
        <BenchmarkPanel />
      </section>

      {/* Rep Handoff Feed */}
      <RepHandoffFeed />
    </div>
  );
}
